/**
 * Camera-rig seek chevron (Need for Speed Underground 2 GPS).
 *
 * Child of the AR camera, a few metres in front, slightly below optical
 * centre. Nose is object +Z so `lookAt` the pin ENU aims it. Hide policy
 * lives in `ar_look.js` (`hideSeekChevron`) — this module only builds the
 * mesh and slerps it.
 *
 * Frame: ENU as (east, up, −north), same as `ar_model.pose`.
 */
import * as THREE from "three"
import {hideSeekChevron, pinNdc, posterHalfNdc} from "./ar_look.js"

/** Metres in front of the camera. */
export const SEEK_DIST_M = 3.2
/** Metres below the optical centre, so the cue sits on pavement, not faces. */
export const SEEK_DOWN_M = 0.32
/** Quaternion slerp rate (1/s). Compass noise is ±53° in the field walk. */
export const SEEK_SLERP = 8
export const SEEK_FADE = 6
export const SEEK_OPACITY = 0.96

const _worldPin = new THREE.Vector3()
const _fromQ = new THREE.Quaternion()
const _toQ = new THREE.Quaternion()

function shortestQuat(from, to) {
  _toQ.copy(to)
  if (from.dot(_toQ) < 0) {
    _toQ.x = -_toQ.x
    _toQ.y = -_toQ.y
    _toQ.z = -_toQ.z
    _toQ.w = -_toQ.w
  }
  return _toQ
}

function chevronShape(scale) {
  const s = new THREE.Shape()
  const w = 0.55 * scale
  const h = 0.4 * scale
  const t = 0.16 * scale
  s.moveTo(-w, -h)
  s.lineTo(0, h)
  s.lineTo(w, -h)
  s.lineTo(w * 0.55, -h)
  s.lineTo(0, h - t * 1.8)
  s.lineTo(-w * 0.55, -h)
  s.closePath()
  return s
}

function chevronLayer(scale, color, z) {
  const geo = new THREE.ExtrudeGeometry(chevronShape(scale), {
    depth: 0.07 * scale,
    bevelEnabled: true,
    bevelThickness: 0.018 * scale,
    bevelSize: 0.018 * scale,
    bevelSegments: 1,
    curveSegments: 1,
  })
  const mat = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity: 0,
    depthWrite: false,
    depthTest: false,
    toneMapped: false,
    side: THREE.DoubleSide,
  })
  const mesh = new THREE.Mesh(geo, mat)
  // Shape +Y is the point. rotation.x = π/2 sends +Y → +Z (lookAt nose).
  // Check: (0, h, 0) → (0, 0, h).
  mesh.rotation.x = Math.PI / 2
  mesh.position.z = z
  return mesh
}

export function createSeekChevron() {
  const group = new THREE.Group()
  group.name = "seek-chevron"
  group.add(chevronLayer(1, 0x38bdf8, 0))
  group.add(chevronLayer(0.78, 0x0369a1, -0.22))
  group.scale.setScalar(1.2)
  group.position.set(0, -SEEK_DOWN_M, -SEEK_DIST_M)
  group.renderOrder = 20
  group.visible = false
  group.userData.opacity = 0
  return group
}

function setOpacity(group, opacity) {
  group.userData.opacity = opacity
  group.traverse((node) => {
    if (node.material && "opacity" in node.material) node.material.opacity = opacity
  })
  group.visible = opacity > 0.02
}

/**
 * Aim and fade the camera-child chevron.
 *
 * `show` is the overlay-allowed bit from the caller. Hide geometry is
 * `hideSeekChevron` (contained, or too-big-and-origin-in-view).
 */
export function tickSeekChevron({
  chevron,
  heading,
  pitch = 0,
  w,
  h,
  enu,
  sizeM,
  planeAspect,
  dt,
  show,
}) {
  if (!chevron) return {hidden: true, opacity: 0}

  const allowed = Boolean(show && enu && heading != null && w > 0 && h > 0)
  let hide = !allowed
  if (allowed) {
    const ndc = pinNdc({heading, pitch, w, h, enu})
    const dist = ndc?.dist ?? Math.hypot(enu.east, enu.north, enu.up)
    const half = posterHalfNdc({sizeM, planeAspect, dist, w, h})
    hide = hideSeekChevron({ndc, ...half})
  }

  const target = hide ? 0 : SEEK_OPACITY
  const fade = 1 - Math.exp(-SEEK_FADE * Math.max(dt, 0))
  const opacity = chevron.userData.opacity + (target - chevron.userData.opacity) * fade
  setOpacity(chevron, opacity)

  if (allowed && enu) {
    _worldPin.set(Number(enu.east) || 0, Number(enu.up) || 0, -(Number(enu.north) || 0))
    _fromQ.copy(chevron.quaternion)
    chevron.lookAt(_worldPin)
    const end = shortestQuat(_fromQ, chevron.quaternion)
    const slerp = 1 - Math.exp(-SEEK_SLERP * Math.max(dt, 0))
    chevron.quaternion.copy(_fromQ).slerp(end, slerp)
  }

  return {hidden: hide, opacity}
}

export function disposeSeekChevron(group) {
  if (!group) return
  group.traverse((node) => {
    node.geometry?.dispose()
    const material = node.material
    if (!material) return
    const list = Array.isArray(material) ? material : [material]
    for (const item of list) item.dispose()
  })
}
